import React, {useState} from "react";
import axios from "axios";
import {
    Box,
    Button,
    CssBaseline,
    Divider,
    FormControl,
    Stack,
    TextField,
    Card as MuiCard,
    Typography,
    Alert,
    CircularProgress
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import Lottie from "lottie-react";
import anime6 from "../animations/anime6.json";

const Card = styled(MuiCard)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignSelf: "center",
    width: "100%",
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: "auto",
    borderRadius: '20px',
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    [theme.breakpoints.up("sm")]: {
        width: "450px"
    },
    ...theme.applyStyles('dark', {
        background: 'rgba(30, 30, 30, 0.9)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
    }),
}));

const ForgotContainer = styled(Stack)(({ theme }) => ({
    minHeight: "100vh",
    padding: theme.spacing(2),
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    position: 'relative',
    [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(4)
    },
    "&::before": {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(45deg, rgba(52, 152, 219, 0.1) 0%, rgba(155, 89, 182, 0.1) 100%)',
        zIndex: 0,
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '25px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    textTransform: 'none',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
        background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    },
    '&:disabled': {
        background: 'linear-gradient(135deg, #a0a0a0 0%, #808080 100%)',
        transform: 'none',
        boxShadow: 'none',
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.8)',
        transition: 'all 0.3s ease',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.9)',
            transform: 'translateY(-1px)',
        },
        '&.Mui-focused': {
            background: 'rgba(255, 255, 255, 1)',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.2)',
        },
    },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
    fontSize: '2.2rem',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textAlign: 'center',
    marginBottom: '8px',
}));

const SubTitle = styled(Typography)(({ theme }) => ({
    color: '#7f8c8d',
    textAlign: 'center',
    fontSize: '14px',
    lineHeight: '1.5',
    marginBottom: '16px',
}));

const StyledLink = styled(Link)(({ theme }) => ({
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    '&:hover': {
        color: '#764ba2',
        textDecoration: 'underline',
    },
}));

function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [emailError, setEmailError] = useState("");

    const validateEmail = () => {
        if (!email || !/\S+@\S+\.\S+/.test(email)) {
            setEmailError("Lütfen geçerli bir e-posta adresi girin.");
            return false;
        }
        setEmailError("");
        return true;
    };

    const handleSendReset = async () => {
        if (!validateEmail()) return;

        try {
            setLoading(true);
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/forgot-password?email=${email}`);
            setMessage("Şifre sıfırlama linki e-posta adresinize gönderildi. Lütfen e-posta kutunuzu kontrol edin.");
            setSuccess(true);
            console.log(res.data);
        } catch (err) {
            setMessage("E-posta adresi bulunamadı veya bir hata oluştu.");
            setSuccess(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="ForgotPasswordPage">
            <CssBaseline enableColorScheme />
            <ForgotContainer direction="column" justifyContent="center">
                <Card variant="outlined">
                    <PageTitle>🔐 Şifremi Unuttum</PageTitle>
                    <SubTitle>
                        E-posta adresinizi girin ve şifre sıfırlama linki gönderelim.
                    </SubTitle>

                    <Lottie
                        animationData={anime6}
                        loop={true}
                        style={{width: '60%', height: '60%', margin: '0 auto'}}
                    />

                    <Divider />

                    {message && (
                        <Alert
                            severity={success ? "success" : "error"}
                            sx={{ borderRadius: '12px', mb: 2 }}
                        >
                            {message}
                        </Alert>
                    )}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <FormControl>
                            <StyledTextField
                                required
                                fullWidth
                                type="email"
                                label="E-posta Adresi"
                                placeholder="your@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                error={!!emailError}
                                helperText={emailError}
                                autoComplete="email"
                            />
                        </FormControl>

                        <StyledButton
                            fullWidth
                            variant="contained"
                            onClick={handleSendReset}
                            disabled={loading || !email}
                        >
                            {loading ? <CircularProgress size={24} /> : "📧 Sıfırlama Linki Gönder"}
                        </StyledButton>
                    </Box>

                    <Divider />

                    <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ color: "gray", fontSize: '14px' }}>
                            Şifrenizi hatırladınız mı? <StyledLink to="/signin">Giriş Yap</StyledLink>
                        </Typography>
                        <Typography sx={{ color: "gray", fontSize: '14px' }}>
                            Hesabınız yok mu? <StyledLink to="/signup">Kayıt Ol</StyledLink>
                        </Typography>
                    </Box>
                </Card>
            </ForgotContainer>
        </div>
    );
}

export default ForgotPasswordPage;